import React from 'react'
import { DiamondIcon, OrderDataIcon, TaxonomyIcon } from 'assests/GmsIcons';
import { ArrowDown, ArrowUp, ChevronDown, Sort } from 'Components/DesignSystem/Iconography/Icons';
import Spinner from 'Components/DesignSystem/Spinner/Spinner';
import { ATTRIBUTE_OVERLAP, CATEGORY, CATEGORY_TYPE, CO_SOLD_CATEGORY, CO_SOLD_CATEGORY_TYPE, CURATED, INVOICE_COUNT, LOADING, REVIEW, UNIFIED_SCORING } from '../constants/CategoryRelevancyConstants';
import TableSkeletonElement from 'Components/GMS/Products/components/TableSkeletonElement';
import NoDataComponent from 'Components/Common/NoDataComponent';
import ReviewDropdown from 'Pages/GMS/Mapping/Components/ReviewDropdown';
import Badge from 'Components/DesignSystem/Badge/Badge';
import Toggle from 'Components/DesignSystem/Toggle/Toggle';
import ThreeDotMenu from './ThreeDotMenu';

/* The glyphs are currentColor (okf/assests 2026-09-08); ink-secondary keeps the
   quiet weight the old #6D788C hardcoded. */
const DataSourceIcon = ({ dataSource }: { dataSource?: CoSoldCategory['data_source'] }) => {
    if (dataSource === 'unified') return <span className="tw-text-ink-secondary tw-inline-flex"><DiamondIcon /></span>;
    if (dataSource === 'order_data') return <span className="tw-text-ink-secondary tw-inline-flex"><OrderDataIcon /></span>;
    if (dataSource === 'taxonomy') return <span className="tw-text-ink-secondary tw-inline-flex"><TaxonomyIcon /></span>;
    return null;
};

type CategoryRelevancyTableProps = {
    categories: Category[] | undefined;
    expandedCategories: Set<string>;
    coSoldCategories: CoSoldCategoryParent[];
    onCategoryExpand: (categoryId: string) => void;
    onToggleCuration: (coSoldCategoryId: string, parentCategoryId: string, isCurated: boolean, reviewStatus: CoSoldCategory['review_status']) => void;
    onUpdateReviewStatus?: (coSoldCategoryId: string, parentCategoryId: string) => void;
    isCuratedTab: boolean;
    rowDensity: RowDensity;
    loadingCategories: boolean;
    loadingCoSoldCategories: (categoryId: string) => boolean;
    handleSort: (column: SortOption) => void;
    sortBy: SortOption;
    sortOrder: SortOrder;
    showAttributeOverlap?: boolean;
    showUnifiedScoring?: boolean;
    showTaxonomyIcon?: boolean;
    disabled?: boolean;
    onOpenCreateCompatibility: (categoryA: { id: string; name: string }, categoryB: { id: string; name: string }) => void;
    onOpenViewCompatibility: (categoryA: { id: string; name: string }, categoryB: { id: string; name: string }) => void;
}

const CategoryRelevancyTable = ({
    categories,
    expandedCategories,
    coSoldCategories,
    onCategoryExpand,
    onToggleCuration,
    onUpdateReviewStatus,
    isCuratedTab,
    rowDensity,
    loadingCategories,
    loadingCoSoldCategories,
    sortBy,
    sortOrder,
    handleSort,
    showAttributeOverlap = false,
    showUnifiedScoring = false,
    showTaxonomyIcon = false,
    disabled = false,
    onOpenCreateCompatibility,
    onOpenViewCompatibility,
}: CategoryRelevancyTableProps) => {
    const getRowPadding = () => {
        switch (rowDensity) {
            case 'compact': return 'tw-py-1';
            case 'comfortable': return 'tw-py-3';
            default: return 'tw-py-2';
        }
    };

    const rowPadding = getRowPadding();
    const parentRowIndent = 'tw-pl-2';

    const headerClasses = "tw-cursor-pointer tw-py-3 tw-px-3 tw-text-left tw-text-sm tw-font-semibold tw-text-ink-secondary tw-align-middle";
    const truncatedCell = "tw-truncate tw-max-w-0 tw-overflow-hidden tw-whitespace-nowrap";

    // Extra conditional columns count
    const extraCols =
        (showTaxonomyIcon ? 1 : 0) +
        (showAttributeOverlap ? 1 : 0) +
        (showUnifiedScoring ? 1 : 0);

    // Base columns: 5 always + 2 raw-only (curated/review) + 1 actions (All tab only) = 8 raw, 5 curated
    const baseCols = isCuratedTab ? 5 : 8;
    const totalColSpan = baseCols + extraCols;

    // Dynamic min-width: 4 flexible columns × 150px + all fixed-width columns
    const fixedColsWidth =
        (showTaxonomyIcon ? 40 : 0) +
        140 + // invoice_count
        (showAttributeOverlap ? 150 : 0) +
        (showUnifiedScoring ? 140 : 0) +
        (!isCuratedTab ? 100 : 0) + // curated
        (!isCuratedTab ? 160 : 0) + // review
        (!isCuratedTab ? 48 : 0);   // actions
    const minTableWidth = 600 + fixedColsWidth;

    // Category header row spans all cols except the Review badge and Actions cols (All tab only)
    const headerRowSpan = isCuratedTab ? totalColSpan : totalColSpan - 2;

    const getColumnWidth = (column: SortOption | 'icon' | 'review'): string => {
        switch (column) {
            case 'icon': return 'tw-w-[40px]';
            case 'invoice_count': return 'tw-w-[135px]';
            case 'attribute_overlap': return 'tw-w-[105px]';
            case 'unified_score': return 'tw-w-[105px]';
            case 'is_curated': return 'tw-w-[100px]';
            case 'review': return 'tw-w-[160px]';
            default: return '';
        }
    };

    const renderHeaderCell = (
        column: SortOption,
        label: string,
        indent: boolean = false,
        sortable: boolean = true,
        useCommonHeaderStyle: boolean = true
    ) => (
        <th
            key={column}
            className={`${useCommonHeaderStyle ? headerClasses : 'tw-cursor-pointer tw-text-left tw-text-sm tw-font-semibold tw-text-ink-secondary'} ${getColumnWidth(column)}`}
            onClick={sortable ? () => handleSort(column) : undefined}
            style={sortable ? undefined : { cursor: 'default' }}
        >
            <div className={`tw-flex tw-items-center ${indent ? 'tw-ml-7' : ''}`}>
                <span>{label}</span>
                {sortable && <span className="tw-ml-1">{sortElement(column)}</span>}
            </div>
        </th>
    );

    /* The DS sort glyph trio — the same three TableHeader draws (currentColor, so
       they inherit the header's ink-secondary). */
    const sortElement = (column: string) => {
        return sortBy === column ? (
            <span className="tw-ml-1 tw-inline-flex">
                {sortOrder === 1 ? (
                    <ArrowUp />
                ) : (
                    <ArrowDown />
                )}
            </span>
        ) : (
            <Sort />
        );
    };

    /* The expand affordance. The context marks a row loading the moment its
       chevron is clicked but only adds it to `expandedCategories` AFTER the
       co-sold fetch resolves — so while the request is in flight the row is
       loading yet NOT expanded, and the in-table loading row (inside
       `isExpanded && …`) can never render. The DS Spinner takes the chevron's
       slot for exactly that window, so the click visibly did something. */
    const expandGlyph = (isExpanded: boolean, isRowLoading: boolean, categoryId: string) => (
        isRowLoading && !isExpanded ? (
            <span className="tw-px-1.5 tw-inline-flex">
                <Spinner size="sm" label="Loading co-sold categories" data-testid={`cosold-loading-${categoryId}`} />
            </span>
        ) : (
            <span className={`tw-px-1.5 tw-inline-flex tw-text-ink-secondary ${isExpanded ? 'tw-rotate-180' : ''}`}><ChevronDown size={16} /></span>
        )
    );

    const findCoSoldCategoriesForCategory = (categoryId: string): CoSoldCategory[] => {
        const parentCategory = coSoldCategories.find(cat => cat.category_id === categoryId);
        return parentCategory?.co_sold_categories || [];
    };

    const formatOverlapValue = (value: number | null | undefined): string => {
        if (value === null || value === undefined) return '-';
        return value.toString();
    };

    if (loadingCategories || !categories) { return <div className=""><TableSkeletonElement /></div> }

    return (
        <div className="tw-border tw-border-solid tw-rounded-md tw-mt-3 tw-border-edge tw-overflow-hidden tw-mb-20">
            <div className="table-container" style={{ width: '100%', height: 'auto', overflowX: 'auto' }}>
                {/* tw-text-ink on the table: cell text must never ride UA inheritance —
                    the dark body rule sets background only (CBM § 4.6 lesson). */}
                <table className="tw-bg-surface-sunken tw-text-ink tw-w-full tw-table-fixed" style={{ minWidth: minTableWidth }}>
                    <thead className="tw-sticky tw-top-0 tw-bg-surface-sunken">
                        <tr>
                            {/* Icon column */}
                            {showTaxonomyIcon && (
                                <th className={`${headerClasses} ${getColumnWidth('icon')} tw-cursor-default`} />
                            )}

                            {renderHeaderCell("category_name", CATEGORY, !showTaxonomyIcon, true, false)}
                            {renderHeaderCell("category_type_name", CATEGORY_TYPE)}
                            {renderHeaderCell("co_sold_category", CO_SOLD_CATEGORY)}
                            {renderHeaderCell("co_sold_category_type", CO_SOLD_CATEGORY_TYPE)}
                            {renderHeaderCell("invoice_count", INVOICE_COUNT)}

                            {/* Attribute Overlap column */}
                            {showAttributeOverlap && renderHeaderCell("attribute_overlap", ATTRIBUTE_OVERLAP)}

                            {/* Unified Scoring column */}
                            {showUnifiedScoring && renderHeaderCell("unified_score", UNIFIED_SCORING)}

                            {!isCuratedTab && renderHeaderCell("is_curated", CURATED, false, false)}
                            {!isCuratedTab && (
                                <th
                                    className={`${headerClasses} ${getColumnWidth('review')} tw-cursor-default`}
                                >
                                    <div className="tw-flex tw-items-center">
                                        <span>{REVIEW}</span>
                                    </div>
                                </th>
                            )}
                            {!isCuratedTab && <th className="tw-w-[48px]" />}
                        </tr>
                    </thead>
                    <tbody>
                        {categories?.map(category => {
                            const isExpanded = expandedCategories?.has(category.category_id);
                            const isRowLoading = loadingCoSoldCategories(category.category_id);
                            const coSoldItems: CoSoldCategory[] = findCoSoldCategoriesForCategory(category.category_id);
                            const parentCategory = coSoldCategories.find(cat => cat.category_id === category.category_id);
                            const reviewPendingCount = category.under_review_count ?? 0;

                            return (
                                <React.Fragment key={category.category_id}>
                                    {/* Category header row */}
                                    <tr data-testid="components-categoryrelevancytable-tr-1"
                                        className="tw-border-b-[0.5px] tw-border-edge tw-cursor-pointer tw-bg-tone-neutral-tint"
                                        onClick={() => onCategoryExpand(category.category_id)}
                                    >
                                        {isCuratedTab ? (
                                            <td colSpan={headerRowSpan} className="tw-p-0">
                                                <div className={`tw-flex tw-items-center tw-gap-2 tw-py-1 ${parentRowIndent}`}>
                                                    {expandGlyph(!!isExpanded, isRowLoading, category.category_id)}
                                                    <span className="tw-justify-start tw-text-ink-secondary tw-text-sm tw-font-semibold">{category.category_name}</span>
                                                </div>
                                            </td>
                                        ) : (
                                            <>
                                                <td colSpan={headerRowSpan} className="tw-p-0">
                                                    <div className={`tw-flex tw-items-center tw-gap-2 tw-py-1 ${parentRowIndent}`}>
                                                        {expandGlyph(!!isExpanded, isRowLoading, category.category_id)}
                                                        <span className="tw-justify-start tw-text-ink-secondary tw-text-sm tw-font-semibold">{category.category_name}</span>
                                                    </div>
                                                </td>
                                                <td className="tw-px-2">
                                                    {reviewPendingCount > 0 && <Badge variant="warning" className="tw-w-fit tw-min-w-[32px]">{reviewPendingCount}</Badge>}
                                                </td>
                                                {/* empty actions cell for parent row */}
                                                <td />
                                            </>
                                        )}
                                    </tr>

                                    {/* Co-sold categories */}
                                    {isExpanded && (
                                        <>
                                            {isRowLoading ? (
                                                <tr>
                                                    <td colSpan={totalColSpan} className="tw-p-4 tw-text-center tw-text-ink-secondary">
                                                        <Spinner size="sm" label={LOADING} showLabel data-testid={`cosold-loading-${category.category_id}`} />
                                                    </td>
                                                </tr>
                                            ) : coSoldItems.length === 0 ? (
                                                <tr>
                                                    <td colSpan={totalColSpan}><NoDataComponent title="No Co-sold Categories Found" /></td>
                                                </tr>
                                            ) : (
                                                coSoldItems.map((coSold: CoSoldCategory, index) => {
                                                    const rowBg = index % 2 === 0 ? "tw-bg-surface" : "tw-bg-surface-sunken";

                                                    return (
                                                        <tr key={coSold.category_id} className="hover:tw-bg-surface-hover">
                                                            {showTaxonomyIcon && (
                                                                <td className={`${rowPadding} ${rowBg} tw-px-2 tw-text-center`}>
                                                                    <DataSourceIcon dataSource={coSold.data_source} />
                                                                </td>
                                                            )}

                                                            <td className={`${rowPadding} ${truncatedCell} ${rowBg}`} title={parentCategory?.category_name}>{parentCategory?.category_name}</td>
                                                            <td className={`${rowPadding} ${truncatedCell} ${rowBg} tw-px-3`} title={parentCategory?.category_type_name}>{parentCategory?.category_type_name || ''}</td>
                                                            <td className={`${rowPadding} ${truncatedCell} ${rowBg} tw-px-3`} title={coSold.category_name}>{coSold.category_name}</td>
                                                            <td className={`${rowPadding} ${truncatedCell} ${rowBg} tw-px-3`} title={coSold.category_type_name}>{coSold.category_type_name}</td>
                                                            <td className={`${rowPadding} ${truncatedCell} ${rowBg} tw-px-3`}>{coSold.invoice_count}</td>

                                                            {/* Attribute Overlap – PRD req 5: only expanded rows show value, req 8: show 0 or - for no data */}
                                                            {showAttributeOverlap && (
                                                                <td className={`${rowPadding} ${truncatedCell} ${rowBg} tw-px-3 tw-text-sm`}>
                                                                    {formatOverlapValue(coSold.attribute_overlap)}
                                                                </td>
                                                            )}

                                                            {/* Unified Scoring */}
                                                            {showUnifiedScoring && (
                                                                <td className={`${rowPadding} ${truncatedCell} ${rowBg} tw-px-3 tw-text-sm`}>
                                                                    {formatOverlapValue(coSold.unified_score)}
                                                                </td>
                                                            )}

                                                            {!isCuratedTab && (
                                                                <td
                                                                    data-testid={`components-categoryrelevancytable-td-2-${index}`}
                                                                    className={`${rowPadding} ${rowBg}`}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    // A pair must be Reviewed before it can be curated; an already-curated
                                                                    // pair stays togglable so it can still be un-curated.
                                                                    title={!coSold.is_curated && coSold.review_status !== 'Reviewed' ? 'Mark as Reviewed before curating' : undefined}
                                                                >
                                                                    <Toggle
                                                                        checked={!!coSold.is_curated}
                                                                        onChange={(val) => onToggleCuration(coSold.category_id, category.category_id, val, coSold.review_status)}
                                                                        label=""
                                                                        disabled={disabled || (!coSold.is_curated && coSold.review_status !== 'Reviewed')}
                                                                    />
                                                                </td>
                                                            )}
                                                            {!isCuratedTab && (
                                                                <td className={`${rowPadding} ${rowBg}`}>
                                                                    <ReviewDropdown
                                                                        value={coSold.review_status === 'Reviewed'}
                                                                        onChange={(value) => { if (value) onUpdateReviewStatus?.(coSold.category_id, category.category_id); }}
                                                                        disabled={disabled || coSold.review_status === 'Reviewed'}
                                                                        itemId={coSold.category_id}
                                                                    />
                                                                </td>
                                                            )}
                                                            {!isCuratedTab && (
                                                                <td className={`${rowPadding} ${rowBg} tw-text-center`}>
                                                                    <ThreeDotMenu
                                                                        disabled={disabled}
                                                                        onCreateCompatibility={() => onOpenCreateCompatibility(
                                                                            { id: category.category_id, name: category.category_name },
                                                                            { id: coSold.category_id, name: coSold.category_name }
                                                                        )}
                                                                        onViewCompatibility={() => onOpenViewCompatibility(
                                                                            { id: category.category_id, name: category.category_name },
                                                                            { id: coSold.category_id, name: coSold.category_name }
                                                                        )}
                                                                    />
                                                                </td>
                                                            )}
                                                        </tr>
                                                    );
                                                })
                                            )}
                                        </>
                                    )}
                                </React.Fragment>
                            );
                        })}

                        {!categories?.length && !loadingCategories && (
                            <tr>
                                <td colSpan={totalColSpan} className="tw-bg-surface"><NoDataComponent title="No Categories Found" /></td>
                            </tr>
                        )}

                        {loadingCategories && (
                            <tr>
                                <td colSpan={totalColSpan} className="tw-p-4 tw-text-center tw-text-ink-secondary">{LOADING}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default CategoryRelevancyTable;
